import React, { useEffect, useState } from 'react';
import { message } from '../Message';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Rating from '@mui/material/Rating';
import { CircularProgress, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';

const Feedbacks = ({ t, firebase }) => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
  const [loading, setLoading] = useState(true);

  const columns = [
    { id: 'actions', label: 'Actions', minWidth: 120 },
    { id: 'status', label: 'Status', minWidth: 100 },
    { id: 'name', label: 'Name', minWidth: 100 },
    { id: 'jobTitle', label: 'Job Title', minWidth: 130 },
    { id: 'rating', label: 'Rating', minWidth: 100 },
    { id: 'message', label: 'Message', minWidth: 200 },
    { id: 'image', label: 'Image', minWidth: 150 },
    { id: 'createdAt', label: 'Created At', minWidth: 150 },
  ];

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const result = await firebase.getFeedbacks();
      const feedbacks = result.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const formattedFeedbacks = feedbacks.map(feedback => ({
        ...feedback,
        createdAt: formatDate(feedback.createdAt),
        status: (
          <Chip
            icon={feedback.isApproved ? <CheckCircleIcon /> : <PendingIcon />}
            label={feedback.isApproved ? 'Approved' : 'Pending'}
            color={feedback.isApproved ? 'success' : 'warning'}
            variant="outlined"
          />
        ),
        actions: (
          <div>
            {!feedback.isApproved && (
              <Button
                variant="outlined"
                color="success"
                size="small"
                onClick={() => handleApprove(feedback)}
                style={{ marginRight: '8px' }}
              >
                Approve
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => handleDeleteClick(feedback.id)}
            >
              Delete
            </Button>
          </div>
        ),
        image: feedback.imageUrl ? (
          <img
            src={feedback.imageUrl}
            alt={`Feedback from ${feedback.name}`}
            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
          />
        ) : (
          'No image'
        ),
        rating: (
          <Rating
            value={feedback.rating}
            readOnly
            size="small"
          />
        )
      }));

      setRows(formattedFeedbacks);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      message.error('Failed to load feedbacks');
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleString();
  };

  const handleApprove = async (feedback) => {
    try {
      await firebase.updateFeedback(feedback.id, {
        ...feedback,
        isApproved: true
      });
      message.success('Feedback approved successfully');
      fetchFeedbacks();
    } catch (error) {
      console.error('Error approving feedback:', error);
      message.error('Failed to approve feedback');
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedFeedbackId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await firebase.deleteFeedback(selectedFeedbackId);
      message.success('Feedback deleted successfully');
      setDeleteDialogOpen(false);
      fetchFeedbacks();
    } catch (error) {
      console.error('Error deleting feedback:', error);
      message.error('Failed to delete feedback');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="my-design">
      <h2>Manage Feedbacks</h2>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow hover key={row.id}>
                    {columns.map((column) => (
                      <TableCell key={column.id}>
                        {row[column.id]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this feedback? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Feedbacks;